/**
 * Organization-role vs platform-role capability helpers.
 *
 * Helm has TWO independent role systems that must never be conflated:
 *
 *  - Organization role (`organization_members.role`, surfaced to the frontend
 *    as `organization.role` / `selectedOrg.role` / `orgRole`): one of
 *    'owner' | 'admin' | 'submissive' | 'member'
 *    (see backend `VALID_MEMBER_ROLES` in authorizationService.ts). This is
 *    per-hierarchy — it's what makes someone a Dominant in THIS org.
 *
 *  - Platform role (`users.role`, surfaced as `user.role` from useAuth): one
 *    of 'member' | 'admin' | 'super_admin'. This gates the internal admin
 *    panel (`/admin/*`) and support tooling — it has NOTHING to do with a
 *    person's Dominant/Submissive standing in any particular organization.
 *
 * 'super_admin' is EXCLUSIVELY a platform-role value. It can never appear in
 * `organization.role` — there is no code path that writes it into
 * `organization_members.role` (confirmed against `VALID_MEMBER_ROLES` and
 * every INSERT/UPDATE of that column). A hand-rolled check like
 * `orgRole === 'super_admin'` is not a bug fix — it's checking a value that
 * structurally cannot occur, so it is silently inert (found across several
 * "super_admin fix" sites during the 2026-07-05 role-gating sweep — see
 * docs/reference/bug-registry.md).
 *
 * The REAL, evidence-backed gap this sweep fixes: `organization.role` CAN
 * legitimately be `'owner'` (e.g. a solo account created via the
 * age-verification flow — `ageVerificationController.ts` inserts the
 * creator as `'owner'`), and backend's `isDominantRole()`
 * (authorizationService.ts) already treats `admin || owner` as
 * Dominant-equivalent. Frontend sites that hand-rolled `role === 'admin'`
 * without `|| role === 'owner'` silently denied Dom capability to org
 * owners. THIS was the real lockout — not a missing super_admin branch.
 */

/**
 * Does this ORGANIZATION role carry Dominant-equivalent capability?
 * Mirrors backend `authorizationService.isDominantRole()` (admin || owner) —
 * the two `organization_members.role` values that constitute "Dom" in a
 * hierarchy.
 *
 * @param orgRole - the current user's role in the organization
 *   (`organization.role` / `selectedOrg.role` / `orgRole` from context).
 * @param platformRole - OPTIONAL: the current user's global platform role
 *   (`user.role` from `useAuth`). Pass this only at sites that intentionally
 *   want a platform super_admin to get Dom capability in every org
 *   (a support-override, not an org-membership fact) — do NOT pass an org
 *   role here by mistake; that is the exact conflation this helper exists
 *   to prevent.
 */
export function isDomRole(
  orgRole?: string | null,
  platformRole?: string | null
): boolean {
  return orgRole === 'admin' || orgRole === 'owner' || platformRole === 'super_admin';
}

/**
 * Does this member carry Dominant-equivalent capability RIGHT NOW, accounting
 * for switch dynamics? Mirrors backend `isEffectivelyDominant()`
 * (authorizationService.ts) — which is what every backend authority gate
 * actually calls, including `requireTaskModifyAccess`.
 *
 * A switch's org role does NOT change when they toggle, so `isDomRole` alone
 * reads a stale answer for them in both directions: it keeps Dom capability
 * for a switch currently active as submissive, and denies it to a switch
 * whose org role is 'member' but who is currently active as dominant
 * (Spec 274). Use this — not `isDomRole` — at any site whose backend
 * counterpart gates on `isEffectivelyDominant`.
 *
 * `dynamicRole`/`activeRole` come from `useActiveRole(orgId, userId)`.
 *
 * NOTE: `web/src/components/praise/QuickPraiseModal.tsx:145` hand-rolls this
 * same predicate inline (pre-dating this helper). Not refactored here to keep
 * this hotfix surgical — a consolidation candidate.
 */
export function isEffectivelyDomRole(
  orgRole?: string | null,
  dynamicRole?: string | null,
  activeRole?: string | null,
  platformRole?: string | null
): boolean {
  if (dynamicRole === 'switch') {
    return activeRole === 'dominant';
  }
  return isDomRole(orgRole, platformRole);
}

/**
 * Does this ORGANIZATION role carry Submissive-equivalent capability?
 * The two `organization_members.role` values that constitute "Sub" in a
 * hierarchy: 'submissive' (the explicit value, assignable via
 * organizationController's member-role-update — see `assertAssignableRole`)
 * and 'member' (the legacy/default plain-member value — same capability
 * level, no explicit Dom/Sub designation chosen).
 *
 * 'owner' and 'admin' are also excluded — they are Dom-equivalent
 * (`isDomRole`), never Sub-equivalent, even though 'owner' can appear
 * alongside a lone submissive-facing UI. There is no platform-role override
 * parameter here: `isDomRole`'s `platformRole` argument exists only for the
 * documented support-override case (a platform super_admin browsing another
 * org getting Dom capability); no equivalent legitimate override exists for
 * Sub capability, so this helper intentionally takes org role only.
 *
 * @param orgRole - the current user's role in the organization
 *   (`organization.role` / `selectedOrg.role` / `orgRole` / `userOrgRole`).
 */
export function isSubRole(orgRole?: string | null): boolean {
  return orgRole === 'member' || orgRole === 'submissive';
}
